import type { ReactNode } from 'react';

/**
 * Markdown do comentário oficial, **sem biblioteca e sem HTML injetado** —
 * portado do `enamed-feedback-lp`, com os mesmos testes.
 *
 * O comentário vem do `student` (`/v1/enamed-feedback/practice-exam` e
 * `/exam/:templateId`) em markdown.
 *
 * Cobre o que um comentário de questão usa: parágrafos (quebra de linha simples
 * vira `<br>`), títulos `#`, listas `-`/`*`/`1.`, citação `>`, linha `---`, e no
 * texto `**negrito**`, `*itálico*`, `` `código` ``, `[link](https://…)` e o link
 * automático `<https://…>` (as bibliografias dos comentários da Aristo usam
 * esse, e ele aparecia cru).
 *
 * **Tudo vira elemento React**, então nada do texto é interpretado como HTML: um
 * `<script>` no comentário aparece escrito, e não roda. Link só sai para
 * `http`, `https` e `mailto` — um `javascript:` fica como texto.
 *
 * Fica de fora o que um comentário raramente tem: tabela, imagem e HTML embutido.
 * Se passarem a aparecer, o caminho é `react-markdown`, e é decisão de
 * dependência.
 */
export function Markdown({
  source,
  className,
}: {
  source: string;
  className?: string;
}) {
  const blocks = parseBlocks(source);
  if (blocks.length === 0) return null;

  return (
    <div className={className}>
      {blocks.map((block, index) => renderBlock(block, index))}
    </div>
  );
}

type Block =
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'heading'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'rule' };

const HEADING = /^#{1,6}\s+(.*)$/;
const UNORDERED = /^\s*[-*+]\s+(.*)$/;
const ORDERED = /^\s*\d+[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;

function parseBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: Block[] = [];
  let current: Block | null = null;

  const flush = () => {
    if (current) blocks.push(current);
    current = null;
  };

  for (const line of lines) {
    if (line.trim() === '') {
      flush();
      continue;
    }

    // A linha `---` vem antes da lista: `- - -` também casaria com item.
    if (RULE.test(line)) {
      flush();
      blocks.push({ kind: 'rule' });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({ kind: 'heading', text: heading[1].trim() });
      continue;
    }

    const unordered = UNORDERED.exec(line);
    const ordered = unordered ? null : ORDERED.exec(line);
    if (unordered || ordered) {
      const isOrdered = ordered !== null;
      const text = (unordered ?? ordered)![1];
      if (current?.kind !== 'list' || current.ordered !== isOrdered) {
        flush();
        current = { kind: 'list', ordered: isOrdered, items: [] };
      }
      current.items.push(text);
      continue;
    }

    const quote = QUOTE.exec(line);
    if (quote) {
      if (current?.kind !== 'quote') {
        flush();
        current = { kind: 'quote', lines: [] };
      }
      current.lines.push(quote[1]);
      continue;
    }

    // Linha comum logo depois de um item continua o item, como no markdown.
    if (current?.kind === 'list') {
      const last = current.items.length - 1;
      current.items[last] = `${current.items[last]} ${line.trim()}`;
      continue;
    }

    if (current?.kind !== 'paragraph') {
      flush();
      current = { kind: 'paragraph', lines: [] };
    }
    current.lines.push(line.trim());
  }

  flush();
  return blocks;
}

function renderBlock(block: Block, key: number): ReactNode {
  switch (block.kind) {
    case 'heading':
      return (
        <p key={key} className="md-heading">
          {renderInline(block.text)}
        </p>
      );
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag key={key} className={block.ordered ? 'md-ol' : 'md-ul'}>
          {block.items.map((item, index) => (
            <li key={index}>{renderInline(item)}</li>
          ))}
        </Tag>
      );
    }
    case 'quote':
      return (
        <blockquote key={key} className="md-quote">
          {withLineBreaks(block.lines)}
        </blockquote>
      );
    case 'rule':
      return <hr key={key} className="md-rule" />;
    case 'paragraph':
      return <p key={key}>{withLineBreaks(block.lines)}</p>;
  }
}

/** Quebra de linha simples vira `<br>`: é assim que comentário é escrito. */
function withLineBreaks(lines: string[]): ReactNode[] {
  return lines.flatMap((line, index) =>
    index === 0
      ? [renderInline(line, `l${index}`)]
      : [<br key={`br${index}`} />, renderInline(line, `l${index}`)],
  );
}

/**
 * Os marcadores de texto, na ordem em que aparecem. O primeiro que casa ganha, e
 * o que está dentro de negrito e itálico é lido de novo — `**negrito com
 * [link](…)**` funciona.
 */
const INLINE =
  /(`[^`]+`)|(\*\*[^*]+\*\*|__[^_]+__)|(\*[^*\s][^*]*\*|_[^_\s][^_]*_)|(\[[^\]]+\]\([^)\s]+\))|(<https?:\/\/[^>\s]+>)/;

const SAFE_LINK = /^(https?:|mailto:)/i;

function renderInline(text: string, keyPrefix = 'i'): ReactNode {
  const nodes: ReactNode[] = [];
  let rest = text;
  let index = 0;

  while (rest.length > 0) {
    const match = INLINE.exec(rest);
    if (!match) {
      nodes.push(rest);
      break;
    }

    if (match.index > 0) nodes.push(rest.slice(0, match.index));
    const [token, code, bold, italic, link, autolink] = match;
    const key = `${keyPrefix}-${index++}`;

    if (code) {
      nodes.push(<code key={key}>{code.slice(1, -1)}</code>);
    } else if (bold) {
      nodes.push(
        <strong key={key}>{renderInline(bold.slice(2, -2), key)}</strong>,
      );
    } else if (italic) {
      nodes.push(<em key={key}>{renderInline(italic.slice(1, -1), key)}</em>);
    } else if (link) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(link)!;
      nodes.push(
        SAFE_LINK.test(href) ? (
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {renderInline(label, key)}
          </a>
        ) : (
          token
        ),
      );
    } else if (autolink) {
      // `<https://…>`: o texto do link é o próprio endereço, sem os sinais.
      const href = autolink.slice(1, -1);
      nodes.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {href}
        </a>,
      );
    }

    rest = rest.slice(match.index + token.length);
  }

  return nodes;
}
